import { Link } from "react-router-dom"
import { Tooltip as ReactTooltip } from "react-tooltip"
import styled from "styled-components"

import tooltipIcon from "assets/svg/tooltip-icon.svg"

import type { TrendDirection } from "./types"

export const Page = styled.div`
  width: min(893px, calc(100% - 48px));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 32px;

  @media only screen and (max-width: 768px) {
    width: calc(100% - 24px);
    padding-bottom: 12px;
  }
`

export const ReportShell = styled.div<{ $backgroundGradient: string }>`
  --monthly-engagement-report-gradient: ${({ $backgroundGradient }) => $backgroundGradient};

  min-height: 100vh;
  background: #f1f5f9;
  padding-bottom: 24px;

  @media only screen and (max-width: 768px) {
    padding-bottom: 12px;
  }
`

export const ReportBody = styled.div`
  padding: 0;

  @media only screen and (max-width: 768px) {
    padding: 0;
  }
`

export const ReportSectionNavigationShell = styled.div`
  position: sticky;
  top: 49px;
  z-index: 9;
  width: 100%;
  padding: 0;
  background: rgb(241 245 249 / 94%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e2e8f0;

  @media only screen and (max-width: 568px) {
    top: 81px;
    padding: 0;
  }
`

export const ReportSectionNavigation = styled.nav`
  width: min(893px, calc(100% - 48px));
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  padding: 8px 0;
  background: transparent;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media only screen and (max-width: 768px) {
    width: calc(100% - 24px);
  }

  @media only screen and (max-width: 568px) {
    padding: 6px 0;
  }
`

export const ReportSectionNavLink = styled.a<{ $active?: boolean }>`
  flex: 0 0 auto;
  padding: 8px 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  color: ${({ $active }) => ($active ? "#FFFFFF" : "#7C8AA5")};
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
  background: ${({ $active }) => ($active ? "var(--monthly-engagement-report-gradient)" : "transparent")};
  box-shadow: ${({ $active }) => ($active ? "0 1px 2px rgb(80 70 180 / 18%)" : "none")};

  &:hover,
  &:focus-visible {
    border-color: transparent;
    background: ${({ $active }) => ($active ? "var(--monthly-engagement-report-gradient)" : "rgb(148 163 184 / 12%)")};
    color: ${({ $active }) => ($active ? "#FFFFFF" : "#5F6F8D")};
    outline: none;
  }
`

export const ReportSectionAnchor = styled.div`
  scroll-margin-top: 88px;
`

export const ReportToolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 8px 32px;
  gap: 16px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;

  @media only screen and (max-width: 568px) {
    padding: 8px 24px;
    gap: 12px;
    flex-direction: column;
    align-items: flex-start;
  }
`

export const ReportToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`

export const ReportToolbarLogo = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 32px;
    width: auto;
    max-width: 160px;
    object-fit: contain;
  }
`

export const ReportToolbarDivider = styled.span`
  width: 1px;
  height: 16px;
  background: #e2e8f0;
  flex-shrink: 0;
`

export const ReportToolbarTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const ReportToolbarTitle = styled.h1`
  margin: 0;
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`

export const ReportToolbarSubtitle = styled.span`
  color: #64748b;
  font-size: 12px;
  line-height: 1.2;
`

export const ReportToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

export const PageToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;

  > button,
  > span {
    white-space: nowrap;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-right: 6px;
    flex-shrink: 0;
  }
`

export const LogoWrapper = styled(Link)`
  align-self: baseline;
  min-width: 100px;
  transition: margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
`

export const StyledLogo = styled.img`
  min-height: 64px;
  max-height: 80px;
  object-fit: contain;
`

export const SummaryCard = styled.section`
  width: 100%;
  min-height: 126px;
  background: #f8fafc;
  border-radius: 20px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`

export const SummaryCover = styled.section<{ $backgroundGradient: string }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 430px;
  padding: 56px 46px 72px;
  border-radius: 0;
  background: ${({ $backgroundGradient }) => $backgroundGradient};
  color: #ffffff;

  @media only screen and (max-width: 768px) {
    min-height: auto;
    padding: 32px 24px;
  }
`

export const SummaryCoverContent = styled.div`
  position: relative;
  z-index: 1;
`

export const SummaryCoverHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media only screen and (max-width: 980px) {
    display: flex;
  }

  @media only screen and (max-width: 560px) {
    align-items: stretch;
  }
`

export const SummaryCoverBody = styled.div`
  min-width: 0;
`

export const SummaryLogoImage = styled.img`
  width: 140px;
  height: auto;
  max-height: 58px;
  max-width: 220px;
  object-fit: contain;
  margin-bottom: 24px;
`

export const SummaryLogoFallback = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 24px;
`

export const SummaryEyebrow = styled.div`
  max-width: 900px;
  margin-bottom: 18px;
  color: #fbfcfe;
  font-size: 28px;
  font-weight: 500;
  line-height: 1.22;

  @media only screen and (max-width: 768px) {
    font-size: 20px;
  }
`

export const SummaryHeadline = styled.h1`
  max-width: 760px;
  margin: 0;
  color: #ffffff;
  font-size: clamp(54px, 6.1vw, 64px);
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: -2px;

  @media only screen and (max-width: 768px) {
    font-size: 36px;
    letter-spacing: -1.5px;
  }
`

export const SummaryContractor = styled.div`
  margin-top: 24px;
  max-width: 900px;
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;

  @media only screen and (max-width: 768px) {
    font-size: 18px;
  }
`

export const SummaryDescription = styled.p`
  max-width: 920px;
  margin: 28px 0 0;
  color: #f3f5ff;
  font-size: 28px;
  line-height: 1.32;

  @media only screen and (max-width: 768px) {
    font-size: 18px;
  }
`

export const SummaryPills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 24px;

  @media only screen and (max-width: 768px) {
    gap: 10px;
  }
`

export const SummaryCoverActions = styled(PageToolbar)`
  margin-top: 0;
  padding-top: 0;
  flex: 0 0 auto;
  align-items: center;

  @media only screen and (max-width: 980px) {
    justify-content: flex-end;
  }
`

export const SummaryActionButton = styled.button<{ $variant: "primary" | "secondary" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 6px 12px;
  border-radius: 20px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.15s ease;

  ${({ $variant }) =>
    $variant === "primary"
      ? `
        background: #42e8ff;
        color: #134f66;
        border: 1px solid #42e8ff;
      `
      : `
        background: #ffffff;
        color: #2db2c8;
        border: 1px solid #42e8ff;
      `}

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  svg path {
    stroke: currentColor;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const SummaryPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 10px 16px;
  background: rgb(255 255 255 / 18%);
`

export const SummaryPillDot = styled.span`
  display: inline-flex;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #35f28a;
  flex-shrink: 0;
`

export const SummaryPillText = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`

export const SummaryPillLabel = styled.span`
  color: #d8dbf2;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 2px;
  line-height: 1.2;
  text-transform: uppercase;
`

export const SummaryInnerCard = styled.div<{ $isTitlePage?: boolean }>`
  margin-top: ${({ $isTitlePage }) => ($isTitlePage ? "54px" : "0")};
  border-radius: ${({ $isTitlePage }) => ($isTitlePage ? "14px" : "0")};
  padding: ${({ $isTitlePage }) => ($isTitlePage ? "32px 36px" : "32px 36px")};
  background: ${({ $isTitlePage }) => ($isTitlePage ? "#ffffff" : "var(--monthly-engagement-report-gradient)")};
  color: #f8fafc;

  @media only screen and (max-width: 768px) {
    margin-top: ${({ $isTitlePage }) => ($isTitlePage ? "32px" : "0")};
    padding: 24px;
  }
`

export const SummaryInnerTitle = styled.div<{ $isTitlePage?: boolean }>`
  color: ${({ $isTitlePage }) => ($isTitlePage ? "#172033" : "#f8fafc")};
  font-size: 20px;
  font-weight: 500;
  line-height: 1.2;
`

export const SummaryInnerSubtitle = styled.div<{ $isTitlePage?: boolean }>`
  margin-top: 4px;
  margin-bottom: 12px;
  color: ${({ $isTitlePage }) => ($isTitlePage ? "#64748b" : "#d8dbf2")};
  font-size: 12px;
  line-height: 1.4;
`

export const SummaryHeroMetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media only screen and (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

export const SummaryHeroMetricCard = styled.div`
  position: relative;
  min-height: 116px;
  overflow: visible;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 18px;
  background: #ffffff;
`

export const SummaryHeroMetricLabel = styled.div`
  margin-bottom: 8px;
  color: #64748b;
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
`

export const SummaryHeroMetricHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  margin-bottom: 8px;
`

export const SummaryHeroMetricTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
`

export const SummaryHeroMetricTitle = styled.div`
  min-width: 0;
  color: #64748b;
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
`

export const SummaryHeroMetricTooltip = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const SummaryHeroMetricValue = styled.div`
  position: relative;
  z-index: 1;
  color: #334155;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.1;
`

export const SummaryHeroMetricSubtext = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.2;
`

export const SummaryTrendInlineIcon = styled.span<{ $direction: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  margin-right: 3px;
  color: ${({ $direction }) => ($direction < 0 ? "#ef4444" : "#22c55e")};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  ${({ $direction }) =>
    $direction < 0 &&
    `
      transform: rotate(180deg);
      transform-origin: center;
    `}
`

export const SummaryHeroMetricPercent = styled.span<{ $direction: number }>`
  color: ${({ $direction }) => ($direction < 0 ? "#ef4444" : "#22c55e")};
  vertical-align: bottom;
`

export const SummaryHeroMetricPeriod = styled.span`
  color: #64748b;
`

export const SummaryTotals = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 16px;
  border-top: 1px solid #e2e8f0;
  background: #eef4fa;
  border-radius: 0 0 10px 10px;

  @media only screen and (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`

export const SummaryTotalItem = styled.div`
  padding: 14px;
  border-right: 1px solid #d4dee9;

  &:last-child {
    border-right: 0;
  }

  @media only screen and (max-width: 680px) {
    border-right: 0;
    border-bottom: 1px solid #d4dee9;

    &:last-child {
      border-bottom: 0;
    }
  }
`

export const SummaryTotalLabel = styled.div`
  margin-bottom: 5px;
  color: #64748b;
  font-size: 10px;
  line-height: 1.2;
  text-transform: uppercase;
`

export const SummaryTotalValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #334155;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.1;
`

export const SummaryTotalTrendIcon = styled.span<{ $direction: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ $direction }) => ($direction < 0 ? "#ef4444" : "#22c55e")};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  ${({ $direction }) =>
    $direction < 0 &&
    `
      transform: rotate(180deg);
      transform-origin: center;
    `}
`

export const SummaryTotalTrendLabel = styled.span`
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
`

export const SummaryTotalSubtext = styled.div`
  margin-top: 2px;
  color: #334155;
  font-size: 11px;
  line-height: 1.2;
`

export const ReportSectionCard = styled.section`
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const EngagementSectionCard = styled.section`
  width: 100%;
  background: var(--monthly-engagement-report-gradient);
  border: 0;
  border-radius: 0;
  padding: 32px 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media only screen and (max-width: 768px) {
    padding: 24px;
  }
`

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const ContractorName = styled.h1`
  margin: 0;
  color: #0f172a;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
`

export const ReportTitle = styled.div`
  color: #64748b;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
`

export const SectionTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
`

export const SectionSubtitle = styled.div`
  margin-top: -2px;
  color: #d8dbf2;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
`

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 20px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  @media only screen and (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

export const Metric = styled.div`
  min-width: 0;
`

export const MetricLabel = styled.div`
  margin-bottom: 5px;
  color: #64748b;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.6px;
  line-height: 12px;
  text-transform: uppercase;
`

export const MetricValue = styled.div`
  color: #0f172a;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

export const ExecutiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 12px;

  @media only screen and (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }

  @media only screen and (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

export const ExecutiveMetricCard = styled.div`
  position: relative;
  min-height: 146px;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
`

export const ExecutiveMetricHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f1f5f9;
`

export const ExecutiveMetricTitle = styled.div`
  color: #0f172a;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

export const ExecutiveTooltipTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: default;
  line-height: 0;
  vertical-align: middle;

  &::after {
    display: inline-block;
    transform: scale(1.4);
    transform-origin: center;
    content: url("${tooltipIcon}");
  }

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`

export const ExecutiveInfoTooltip = styled(ReactTooltip)`
  z-index: 9999;
  max-width: 280px;
  padding: 4px 10px !important;
  border: 1px solid #e2e8f0;
  border-radius: 6px !important;
  background: #ffffff !important;
  color: #475569 !important;
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  line-height: 1.3 !important;
  text-align: left;
  box-shadow:
    0 1px 2px 0 rgb(226 232 240 / 24%),
    0 12px 24px 0 rgb(148 163 184 / 12%);
`

export const ExecutiveMetricValue = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 12px;
  color: #475569;
  font-size: 28px;
  font-weight: 500;
  line-height: 34px;
`

export const MetricTrend = styled.div<{ $direction?: TrendDirection }>`
  position: relative;
  z-index: 1;
  color: ${({ $direction }) => {
    if ($direction === "down") return "#ef4444"
    if ($direction === "flat") return "#64748b"
    return "#22c55e"
  }};
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
`

export const TrendChart = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 74%;
  height: 82px;
  pointer-events: none;
`

export const EngagementKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 16px;

  @media only screen and (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

export const CallsSectionCard = styled.section`
  width: 100%;
  background: var(--monthly-engagement-report-gradient);
  border: 0;
  border-radius: 0;
  padding: 32px 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media only screen and (max-width: 768px) {
    padding: 24px;
  }
`

export const CallsSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  @media only screen and (max-width: 560px) {
    flex-direction: column;
  }
`

export const CallsSectionTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
`

export const CallsSectionSubtitle = styled.div`
  margin-top: 4px;
  color: #d8dbf2;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`

export const CallsSourceButton = styled.div`
  flex: 0 0 auto;
  padding: 7px 12px;
  border-radius: 8px;
  background: #42d8e8;
  color: #0f172a;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
`

export const CallsSectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const CallsMetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(150px, 1fr));
  gap: 14px;

  @media only screen and (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

export const CallsMetricCard = styled.div`
  min-height: 120px;
  padding: 18px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
`

export const CallsMetricHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

export const CallsMetricTitle = styled.div`
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  line-height: 16px;
  text-transform: uppercase;
`

export const CallsMetricSourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border-radius: 4px;
  background: #22d3ee;
  color: #0f172a;
  font-family: monospace;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: none;
  position: relative;
  bottom: 2px;
`

export const CallsMetricInfo = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #eef2ff;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`

export const CallsMetricValue = styled.div`
  margin-top: 16px;
  color: #0f172a;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
`

export const CallsMetricTrend = styled.div<{ $direction?: TrendDirection }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 5px;
  color: ${({ $direction }) => {
    if ($direction === "down") return "#ef4444"
    if ($direction === "flat") return "#64748b"
    return "#16a34a"
  }};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  svg {
    width: 12px;
    height: 12px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`

export const CallsMetricTrendValue = styled.span<{ $direction?: TrendDirection }>`
  color: ${({ $direction }) => {
    if ($direction === "down") return "#ef4444"
    if ($direction === "flat") return "#64748b"
    return "#16a34a"
  }};
`

export const CallsMetricTrendPeriod = styled.span`
  color: #64748b;
`

export const CallsSectionSummary = styled.p`
  margin: 14px 0 0;
  color: #d8dbf2;
  font-size: 12px;
  line-height: 18px;
`

export const EngagementKpiCardContainer = styled.div`
  position: relative;
  min-height: 116px;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
`

export const EngagementKpiTitle = styled.div`
  color: #0f172a;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`

export const EngagementKpiSubtitle = styled.div`
  margin-top: 2px;
  color: #64748b;
  font-size: 9px;
  font-weight: 400;
  line-height: 12px;
`

export const EngagementKpiValue = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 18px;
  color: #475569;
  font-size: 24px;
  font-weight: 500;
  line-height: 30px;
`

export const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 18px;
`

export const PanelTitle = styled.div`
  margin-bottom: 14px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`

export const WeeklyTrendPanel = styled(Panel)`
  border-radius: 16px;
  padding: 16px 18px 14px;
`

export const WeeklyTrendTitle = styled(PanelTitle)`
  margin: 0 0 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  line-height: 18px;
`

export const WeeklyChart = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr;
  height: 222px;
  width: 100%;
`

export const YAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 8px 25px 0;
  color: #8fa3bf;
  font-size: 9px;
  line-height: 12px;
  text-align: right;
`

export const ChartArea = styled.div<{ $columns: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  align-items: end;
  border-left: 1px solid #d7e0ec;
  border-bottom: 1px solid #d7e0ec;
  background: linear-gradient(to right, #d7e0ec 1px, transparent 1px) 0 0 / calc(100% / ${({ $columns }) => $columns})
    100%;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: calc(100% - 25px);
    background: #d7e0ec;
  }
`

export const ChartTooltip = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  z-index: 2;
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  min-width: 112px;
  padding: 8px 10px;
  background: rgba(248, 250, 252, 0.98);
  border: 1px solid rgba(203, 213, 225, 0.95);
  border-radius: 10px;
  color: #0f172a;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 8px));

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -5px;
    width: 10px;
    height: 10px;
    background: rgba(248, 250, 252, 0.98);
    border-right: 1px solid rgba(203, 213, 225, 0.95);
    border-bottom: 1px solid rgba(203, 213, 225, 0.95);
    box-shadow: 4px 4px 12px rgba(15, 23, 42, 0.08);
    transform: translateX(-50%) rotate(45deg);
  }
`

export const ChartTooltipLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  color: #475569;
`

export const ChartTooltipValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.1;
`

export const WeekGroup = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 0;
  padding: 0 clamp(4px, 1.5vw, 24px) 25px;
`

export const Bar = styled.div<{ $height: number; $color: string; $seriesCount: number }>`
  flex: 1 1 0;
  max-width: ${({ $seriesCount }) => ($seriesCount > 2 ? "36px" : "64px")};
  height: ${({ $height }) => $height}%;
  min-height: 8px;
  background: ${({ $color }) => $color};
`

export const WeekLabel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  color: #8fa3bf;
  font-size: 9px;
  line-height: 12px;
  text-align: center;
`

export const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
`

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #64748b;
  font-size: 9px;
  line-height: 12px;
`

export const LegendSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  background: ${({ $color }) => $color};
`

export const DevicesSubtitle = styled.div`
  margin-top: -10px;
  margin-bottom: 14px;
  color: #64748b;
  font-size: 9px;
  line-height: 12px;
`

export const DeviceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
`

export const TableHead = styled.thead`
  color: #64748b;
  background: #f1f7ff;
`

export const TableHeaderCell = styled.th`
  padding: 9px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 10px;
  line-height: 1.2;
`

export const TableCell = styled.td`
  padding: 11px 12px;
  color: #0f172a;
  font-weight: 300 !important;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
`

export const DeviceIdCellContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  > span {
    font-weight: 300 !important;
  }
`

export const TopBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 18px;
  padding: 0 8px;
  border: 1px solid #22c55e;
  border-radius: 999px;
  color: #16a34a;
  background: #dcfce7;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  text-transform: uppercase;
`

export const MobileKpiGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const MobileGroupTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

export const PartnerCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
`

export const PartnerTitle = styled.div`
  margin-bottom: 22px;
  color: #0f172a;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

export const PartnerSourceLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const PartnerAnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 16px;

  @media only screen and (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

export const PartnerChannelCard = styled.div`
  padding: 16px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
`

export const PartnerChannelTitle = styled.div`
  margin-bottom: 16px;
  color: #0f172a;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

export const PartnerMetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(90px, 1fr));
  gap: 8px;
`

export const PartnerMetricGridSingle = styled(PartnerMetricGrid)`
  grid-template-columns: 1fr;
`

export const PartnerMetricBox = styled.div<{ $wide?: boolean }>`
  padding: 10px 12px;
  background: #ffffff;
  border-radius: 6px;
  ${({ $wide }) => $wide && `grid-column: 1 / -1;`}
`

export const PartnerMetricLabel = styled.div`
  margin-bottom: 4px;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.3px;
  line-height: 13px;
  text-transform: uppercase;
`

export const PartnerMetricValue = styled.div`
  color: #0f172a;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`
